import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { 
  CREATE_GOAL, 
  UPDATE_GOAL, 
  DELETE_GOAL, 
  LIST_GOALS,
  GET_ALL_CATEGORIES,
  GET_ALL_LEVELS
} from '../graphql/goalOperations';
import { 
  TrashIcon, 
  PencilIcon, 
  PlusIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import ManagementModal from '../components/ManagementModal';

const GoalsManagement = () => {
  // Theme hook
  const { isDarkMode } = useTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [goalName, setGoalName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [levelId, setLevelId] = useState('');
  const [editingGoal, setEditingGoal] = useState(null);
  const [page, setPage] = useState(1);
  
  // Filter states
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterLevelId, setFilterLevelId] = useState('');

  // Compute filters
  const filters = {
    ...(filterName && { name: filterName }),
    ...(filterCategoryId && { categoryId: filterCategoryId }),
    ...(filterLevelId && { levelId: filterLevelId })
  };

  // Fetch goals
  const { 
    loading: goalsLoading, 
    error: goalsError, 
    data: goalsData, 
    refetch: refetchGoals 
  } = useQuery(LIST_GOALS, {
    variables: { page, limit: 10, filters },
    onError: (error) => handleGraphQLError(error, 'Failed to load goals')
  });

  // Fetch categories
  const { 
    loading: categoriesLoading, 
    data: categoriesData 
  } = useQuery(GET_ALL_CATEGORIES);

  // Fetch levels
  const { 
    loading: levelsLoading, 
    data: levelsData 
  } = useQuery(GET_ALL_LEVELS);

  // Mutations
  const [createGoal] = useMutation(CREATE_GOAL, {
    onCompleted: () => {
      resetForm();
      refetchGoals();
      toast.success('Goal created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create goal')
  });

  const [updateGoal] = useMutation(UPDATE_GOAL, {
    onCompleted: () => {
      resetForm();
      refetchGoals();
      toast.success('Goal updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update goal')
  });

  const [deleteGoal] = useMutation(DELETE_GOAL, {
    onCompleted: () => {
      refetchGoals();
      toast.success('Goal deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete goal')
  });

  // Reset form and filters
  const resetForm = () => {
    setGoalName('');
    setCategoryId('');
    setLevelId('');
    setEditingGoal(null);
    setIsModalOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilterName('');
    setFilterCategoryId('');
    setFilterLevelId('');
    setIsFilterOpen(false);
  };

  // Apply filters
  const applyFilters = () => {
    setPage(1); // Reset to first page when applying filters
    refetchGoals();
    setIsFilterOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!goalName.trim()) {
      toast.error('Goal name cannot be empty');
      return;
    }

    const input = {
      name: goalName.trim(),
      categoryId: categoryId || null,
      levelId: levelId || null
    };

    if (editingGoal) {
      updateGoal({
        variables: {
          updateGoalId: editingGoal.id,
          input
        }
      });
    } else {
      createGoal({
        variables: { input }
      });
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setGoalName(goal.name);
    setCategoryId(goal.categoryId);
    setLevelId(goal.levelId);
    setIsModalOpen(true);
  };

  const handleDelete = (goalId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this goal?');
    if (confirmDelete) {
      deleteGoal({
        variables: { deleteGoalId: goalId }
      });
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Loading and error states
  if (goalsLoading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading goals...
    </div>
  );

  if (goalsError) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading goals
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Add Goal Button */}
      <div className="flex justify-end mb-4">
        <button 
          onClick={() => {
            setEditingGoal(null);
            setIsModalOpen(true);
          }}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg
            ${isDarkMode 
              ? 'bg-blue-600 text-white hover:bg-blue-500' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
            transition
          `}
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Goal</span>
        </button>
      </div>

      {/* Goals Management Modal */}
      <ManagementModal
        isOpen={isModalOpen}
        onClose={() => {
          resetForm();
          setIsModalOpen(false);
        }}
        onSubmit={handleSubmit}
        title={editingGoal ? 'Edit Goal' : 'Create New Goal'}
      >
        {/* Goal Name Input */}
        <div>
          <label 
            htmlFor="goalName" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Goal Name
          </label>
          <input
            type="text"
            id="goalName"
            value={goalName}
            onChange={(e) => setGoalName(e.target.value)}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter goal name"
            required
          />
        </div>

        {/* Category Dropdown */}
        <div>
          <label 
            htmlFor="category" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Category (Optional)
          </label>
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
          >
            <option value="">Select Category</option>
            {categoriesData?.getAllCategories?.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Level Dropdown */}
        <div>
          <label 
            htmlFor="level" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Level (Optional)
          </label>
          <select
            id="level"
            value={levelId}
            onChange={(e) => setLevelId(e.target.value)}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
          >
            <option value="">Select Level</option>
            {levelsData?.getAllLevels?.map((level) => (
              <option key={level.id} value={level.id}>
                {level.name}
              </option>
            ))}
          </select>
        </div>
      </ManagementModal>

      {/* Goals List */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Goals List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {goalsData?.listGoals?.totalCount || 0} goals
          </div>
        </div>

        {/* Goals Table */}
        <div className="overflow-x-auto">
          {goalsData?.listGoals?.goals.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No goals found
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className={`
                  ${isDarkMode 
                    ? 'bg-gray-700 text-gray-300' 
                    : 'bg-gray-100 text-gray-600'
                  } 
                  text-sm
                `}>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Category</th>
                  <th className="px-6 py-3 text-left">Level</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-left">Updated At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {goalsData.listGoals.goals.map((goal) => (
                  <tr 
                    key={goal.id} 
                    className={`
                      border-b 
                      ${isDarkMode 
                        ? 'border-gray-700 hover:bg-gray-700' 
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {goal.name}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {categoriesData?.getAllCategories?.find(c => c.id === goal.categoryId)?.name || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {levelsData?.getAllLevels?.find(l => l.id === goal.levelId)?.name || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(goal.createdAt)}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(goal.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button 
                          onClick={() => handleEdit(goal)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700' 
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(goal.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700' 
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center px-6 py-4">
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Page {page} of {goalsData?.listGoals?.totalPages || 1}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className={`
                px-4 py-2 
                ${isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }
                rounded-lg 
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-300
              `}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === (goalsData?.listGoals?.totalPages || 1)}
              className={`
                px-4 py-2 
                ${isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }
                rounded-lg 
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-300
              `}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalsManagement;
